import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  Link,
  useNavigate
} from 'react-router-dom';

import './AdminEvents.scss';

export default function AdminEvents() {
  const navigate =
    useNavigate();

  const apiBaseUrl = useMemo(() => {
    const envUrl =
      process.env.REACT_APP_API_URL ||
      '';

    return envUrl.endsWith('/')
      ? envUrl.slice(0, -1)
      : envUrl;
  }, []);

  const token =
    localStorage.getItem(
      'tg_admin_token'
    );

  const [
    events,
    setEvents
  ] = useState([]);

  const [
    search,
    setSearch
  ] = useState('');

  const [
    view,
    setView
  ] = useState('upcoming');

  const [
    typeFilter,
    setTypeFilter
  ] = useState('all');

  const [
    statusFilter,
    setStatusFilter
  ] = useState('all');

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    error,
    setError
  ] = useState('');

  useEffect(() => {
    const fetchEvents =
      async () => {
        setLoading(true);
        setError('');

        try {
          const response =
            await fetch(
              `${apiBaseUrl}/admin/events`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          const data =
            await response.json();

          if (!response.ok) {
            throw new Error(
              data?.message ||
                'Unable to load events.'
            );
          }

          setEvents(
            data.events || []
          );
        } catch (err) {
          setError(
            err.message ||
              'Unable to load events.'
          );
        } finally {
          setLoading(false);
        }
      };

    fetchEvents();
  }, [
    apiBaseUrl,
    token
  ]);

  const types =
    useMemo(() => {
      return [
        ...new Set(
          events
            .map(
              (event) =>
                event.type
            )
            .filter(Boolean)
        )
      ].sort();
    }, [events]);

  const statuses =
    useMemo(() => {
      return [
        ...new Set(
          events
            .map(
              (event) =>
                event.status
            )
            .filter(Boolean)
        )
      ].sort();
    }, [events]);

  const filteredEvents =
    useMemo(() => {
      const now =
        new Date();

      const normalizedSearch =
        search
          .trim()
          .toLowerCase();

      return events.filter(
        (event) => {
          const eventDate =
            new Date(
              event.datetime
            );

          const matchesView =
            view === 'all' ||
            (
              view === 'upcoming' &&
              eventDate >= now
            ) ||
            (
              view === 'past' &&
              eventDate < now
            );

          const matchesSearch =
            !normalizedSearch ||
            event.title
              ?.toLowerCase()
              .includes(
                normalizedSearch
              ) ||
            event.venue
              ?.toLowerCase()
              .includes(
                normalizedSearch
              ) ||
            event.city
              ?.toLowerCase()
              .includes(
                normalizedSearch
              );

          const matchesType =
            typeFilter === 'all' ||
            event.type ===
              typeFilter;

          const matchesStatus =
            statusFilter === 'all' ||
            event.status ===
              statusFilter;

          return (
            matchesView &&
            matchesSearch &&
            matchesType &&
            matchesStatus
          );
        }
      );
    }, [
      events,
      search,
      view,
      typeFilter,
      statusFilter
    ]);

  const upcomingCount =
    events.filter(
      (event) =>
        new Date(
          event.datetime
        ) >= new Date()
    ).length;

  const formatDate = (
    value
  ) => {
    if (!value) {
      return '—';
    }

    return new Date(
      value
    ).toLocaleDateString(
      undefined,
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }
    );
  };

  const formatTime = (
    value
  ) => {
    if (!value) {
      return '';
    }

    return new Date(
      value
    ).toLocaleTimeString(
      undefined,
      {
        hour: 'numeric',
        minute: '2-digit'
      }
    );
  };

  return (
    <div className="admin-events-page">
      <section className="admin-events-heading">
        <div>
          <p className="admin-events-heading__eyebrow">
            Community Operations
          </p>

          <h2>
            Events
          </h2>

          <p>
            Create, manage and review
            The Tour Golfers real-world
            events.
          </p>
        </div>

        <Link
          to="/admin/events/new"
          className="admin-events-heading__button"
        >
          + Create Event
        </Link>
      </section>

      <section className="admin-event-summary">
        <div className="admin-event-summary__card">
          <span>
            Total Events
          </span>

          <strong>
            {events.length}
          </strong>
        </div>

        <div className="admin-event-summary__card">
          <span>
            Upcoming
          </span>

          <strong>
            {upcomingCount}
          </strong>
        </div>

        <div className="admin-event-summary__card">
          <span>
            Total Reservations
          </span>

          <strong>
            {events.reduce(
              (
                total,
                event
              ) =>
                total +
                Number(
                  event.reserved_count ||
                    0
                ),
              0
            )}
          </strong>
        </div>

        <div className="admin-event-summary__card">
          <span>
            Member Only
          </span>

          <strong>
            {
              events.filter(
                (event) =>
                  event.is_member_only
              ).length
            }
          </strong>
        </div>
      </section>

      <div className="admin-event-view-tabs">
        <button
          type="button"
          className={
            view === 'upcoming'
              ? 'is-active'
              : ''
          }
          onClick={() =>
            setView(
              'upcoming'
            )
          }
        >
          Upcoming
        </button>

        <button
          type="button"
          className={
            view === 'past'
              ? 'is-active'
              : ''
          }
          onClick={() =>
            setView('past')
          }
        >
          Past
        </button>

        <button
          type="button"
          className={
            view === 'all'
              ? 'is-active'
              : ''
          }
          onClick={() =>
            setView('all')
          }
        >
          All
        </button>
      </div>

      <section className="admin-event-filters">
        <div className="admin-event-filters__search">
          <label>
            Search
          </label>

          <input
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            placeholder="Event, venue or city..."
          />
        </div>

        <div className="admin-event-filters__field">
          <label>
            Type
          </label>

          <select
            value={
              typeFilter
            }
            onChange={(event) =>
              setTypeFilter(
                event.target.value
              )
            }
          >
            <option value="all">
              All Types
            </option>

            {types.map(
              (type) => (
                <option
                  key={type}
                  value={type}
                >
                  {type.replaceAll(
                    '_',
                    ' '
                  )}
                </option>
              )
            )}
          </select>
        </div>

        <div className="admin-event-filters__field">
          <label>
            Status
          </label>

          <select
            value={
              statusFilter
            }
            onChange={(event) =>
              setStatusFilter(
                event.target.value
              )
            }
          >
            <option value="all">
              All Statuses
            </option>

            {statuses.map(
              (status) => (
                <option
                  key={status}
                  value={status}
                >
                  {status.replaceAll(
                    '_',
                    ' '
                  )}
                </option>
              )
            )}
          </select>
        </div>
      </section>

      {error ? (
        <div className="admin-events-error">
          {error}
        </div>
      ) : null}

      <section className="admin-events-table-card">
        <div className="admin-events-table-card__header">
          <div>
            <h3>
              Event Calendar
            </h3>

            <span>
              {
                filteredEvents.length
              }{' '}
              results
            </span>
          </div>
        </div>

        {loading ? (
          <div className="admin-events-empty">
            Loading events...
          </div>
        ) : filteredEvents.length ===
          0 ? (
          <div className="admin-events-empty">
            No events found.
          </div>
        ) : (
          <div className="admin-events-table-wrapper">
            <table className="admin-events-table">
              <thead>
                <tr>
                  <th>
                    Event
                  </th>

                  <th>
                    Date
                  </th>

                  <th>
                    Venue
                  </th>

                  <th>
                    Type
                  </th>

                  <th>
                    Reservations
                  </th>

                  <th>
                    Capacity
                  </th>

                  <th>
                    Status
                  </th>

                  <th>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredEvents.map(
                  (event) => (
                    <tr
                      key={
                        event.event_id
                      }
                    >
                      <td>
                        <div className="admin-event-name">
                          <strong>
                            {event.title}
                          </strong>

                          <span>
                            {formatTime(
                              event.datetime
                            )}
                          </span>
                        </div>
                      </td>

                      <td>
                        {formatDate(
                          event.datetime
                        )}
                      </td>

                      <td>
                        <div className="admin-event-location">
                          <strong>
                            {event.venue ||
                              '—'}
                          </strong>

                          <span>
                            {event.city ||
                              '—'}
                          </span>
                        </div>
                      </td>

                      <td>
                        {event.type
                          ?.replaceAll(
                            '_',
                            ' '
                          ) ||
                          '—'}
                      </td>

                      <td>
                        {event.reserved_count ||
                          0}
                      </td>

                      <td>
                        {event.capacity ??
                          '—'}
                      </td>

                      <td>
                        <span className="admin-event-status">
                          {event.status
                            ?.replaceAll(
                              '_',
                              ' '
                            ) ||
                            '—'}
                        </span>
                      </td>

                      <td>
                        <button
                          type="button"
                          className="admin-event-manage-button"
                          onClick={() =>
                            navigate(
                              `/admin/events/${event.event_id}`
                            )
                          }
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}